import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import {useDetectScrollDirection} from "../../common/helpers";
import LanguageSelectionMenu from "./LanguageSelectionMenu";
import {Collection} from "../../types/types";
import DownloadMenu from "./DownloadMenu";
import {Box, useTheme} from "@mui/material";
import CombinedDownloadMenu from "./CombinedDownloadMenu";

interface HeaderProps {
    collection: Collection,
    locale: string,
    setLocale: (locale: string) => void,
}


const Header = ({collection, locale, setLocale}: HeaderProps) => {

    const scrollDirection = useDetectScrollDirection();

    const maisonTrigram = collection.maison.toUpperCase();
    // one of the Maison logos is a png for some reason
    const logoPath = `/logo/${maisonTrigram}.${maisonTrigram === "MFO" ? "png" : "svg"}`;

    const availableLocales = React.useMemo(() => {
        return Object.keys(collection.assets[0]?.metadata?.translatable ?? ["en-US"]);
    }, [collection]);

    const isSingleDownloadMenuPresent = (
        (!!collection.archives?.image ? 1 : 0) +
        (!!collection.archives?.video ? 1 : 0) +
        (!!collection.archives?.document ? 1 : 0)
    ) === 1;

    const theme = useTheme();

    return (
        <AppBar position="sticky" sx={{
            background: "linear-gradient(0deg, rgba(230,230,230,1) 0%, rgba(255,255,255,1) 40%)",
            color: "#000",
            transition: "top 0.5s",
            ...(scrollDirection === "down" ? {top: "-7rem"} : {})
        }}>
            <Container maxWidth="xl">
                <Toolbar disableGutters
                         sx={{display: "flex", flexDirection: {xs: "column", md: "row"}, overflow: "hidden"}}>
                    <Box sx={{
                        alignItems: "center",
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: "row",
                        width: "100%",
                        my: {xs: "0.5rem", md: 0}
                    }}>
                        <Box sx={{display: {xs: "none", md: "block"}}}>
                            <img src={logoPath}
                                 style={{
                                     minWidth: "250px",
                                     maxHeight: "2.5rem",
                                     maxWidth: "3.5rem",
                                     padding: "0.5rem 2rem 0.5rem 0",
                                     objectFit: "contain"
                                 }}
                                 alt="Maison Logo"/>
                        </Box>
                        <Box sx={{display: {xs: "block", md: "none"}}}>
                            <img src={logoPath} width="150" style={{maxHeight: "1.75rem"}}
                                 alt="Maison Logo"/>
                        </Box>
                        <Typography
                            noWrap
                            title={collection.collectionName || ""}
                            sx={{
                                mr: 2,
                                display: {xs: "none", md: "flex"},
                                fontWeight: 500,
                                letterSpacing: ".045rem",
                                color: "inherit",
                                textDecoration: "none",
                                pt: ".3rem",
                                fontSize: "1.25rem",
                            }}
                        >
                            {collection.collectionName || ""}
                        </Typography>
                        <Box sx={{flexShrink: 0, ml: "auto", display: "flex", flexDirection: "row", gap: "0.5rem"}}>
                            {isSingleDownloadMenuPresent ? (<>
                                    <DownloadMenu items={collection.archives?.image}
                                                  archiveType="images"
                                                  label="Download images"
                                                  locale={locale}/>
                                    <DownloadMenu items={collection.archives?.video}
                                                  archiveType="videos"
                                                  label="Download videos"
                                                  locale={locale}/>
                                    <DownloadMenu items={collection.archives?.document}
                                                  archiveType="documents"
                                                  label="Download documents"
                                                  locale={locale}/>
                                </>
                            ) : (
                                <CombinedDownloadMenu
                                    items={{
                                        document: collection.archives?.document ?? {},
                                        image: collection.archives?.image ?? {},
                                        video: collection.archives?.video ?? {},
                                    }}
                                    locale={locale}/>
                            )}
                            <LanguageSelectionMenu
                                availableLocales={availableLocales}
                                locale={locale}
                                setLocale={setLocale}
                            />
                        </Box>
                    </Box>
                    <Typography
                        noWrap
                        sx={{
                            display: {xs: "inline", md: "none"},
                            color: theme.palette.text.secondary,
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            textDecoration: "none",
                            my: "0.4rem",
                        }}
                    >
                        {collection.collectionName || ""}
                    </Typography>
                </Toolbar>
            </Container>
        </AppBar>
    );
}
export default Header;